import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Download, FileSpreadsheet, FileText, Calendar, TrendingUp,
  Award, Target, Book, Clock, CheckCircle, AlertCircle
} from 'lucide-react';
import { client } from '../api/client';
import { useAuthStore } from '../store/authStore';

interface ExportSummary {
  practiceRecords: number;
  mistakes: number;
  badges: number;
  challengesCompleted: number;
  dateRange: {
    start: string | null;
    end: string | null;
  };
}

export function ExportPage() {
  const { user } = useAuthStore();
  const [summary, setSummary] = useState<ExportSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportType, setExportType] = useState<'records' | 'mistakes'>('records');
  const [dateRange, setDateRange] = useState({
    start: '',
    end: '',
  });
  const [exportSuccess, setExportSuccess] = useState(false);

  useEffect(() => {
    if (user) {
      loadSummary();
    }
  }, [user]);

  async function loadSummary() {
    setLoading(true);
    try {
      const res = await client.api.fetch('/api/export/summary');
      const data = await res.json();
      setSummary(data.data);
    } catch (error) {
      console.error('Failed to load summary:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleExport() {
    setExporting(true);
    setExportSuccess(false);
    
    try {
      const params = new URLSearchParams();
      if (dateRange.start) params.set('start', dateRange.start);
      if (dateRange.end) params.set('end', dateRange.end);
      
      const endpoint = exportType === 'mistakes' 
        ? '/api/mistakes/export' 
        : `/api/export/records?${params.toString()}`;
      
      const res = await client.api.fetch(endpoint);
      const blob = await res.blob();
      
      // Download file
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${exportType === 'mistakes' ? 'mistakes' : 'learning_records'}_${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      setExportSuccess(true);
      setTimeout(() => setExportSuccess(false), 3000);
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setExporting(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-slate-900 mb-2 flex items-center gap-3">
            <Download className="w-8 h-8 text-blue-500" />
            学习记录导出
          </h1>
          <p className="text-slate-600">导出你的学习数据，支持CSV格式</p>
        </motion.div>

        {/* Summary cards */}
        {summary && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
          >
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Book className="w-5 h-5 text-blue-600" />
                </div>
              </div>
              <div className="text-2xl font-bold text-slate-900">{summary.practiceRecords}</div>
              <div className="text-sm text-slate-500">练习记录</div>
            </div>
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
              </div>
              <div className="text-2xl font-bold text-slate-900">{summary.mistakes}</div>
              <div className="text-sm text-slate-500">错题记录</div>
            </div>
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Award className="w-5 h-5 text-amber-600" />
                </div>
              </div>
              <div className="text-2xl font-bold text-slate-900">{summary.badges}</div>
              <div className="text-sm text-slate-500">获得徽章</div>
            </div>
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                  <Target className="w-5 h-5 text-orange-600" />
                </div>
              </div>
              <div className="text-2xl font-bold text-slate-900">{summary.challengesCompleted}</div>
              <div className="text-sm text-slate-500">完成挑战</div>
            </div>
          </motion.div>
        )}

        {/* Export options */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-3xl shadow-lg p-6 mb-6"
        >
          <h2 className="text-lg font-semibold text-slate-800 mb-4">选择导出内容</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <button
              onClick={() => setExportType('records')}
              className={`p-5 rounded-2xl border-2 text-left transition-all ${
                exportType === 'records'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-slate-200 hover:border-blue-300'
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <FileSpreadsheet className={`w-6 h-6 ${exportType === 'records' ? 'text-blue-500' : 'text-slate-400'}`} />
                <span className="font-semibold text-slate-800">全部学习记录</span>
              </div>
              <p className="text-sm text-slate-600">包含所有练习记录、答题详情、学习时长等</p>
            </button>
            
            <button
              onClick={() => setExportType('mistakes')}
              className={`p-5 rounded-2xl border-2 text-left transition-all ${
                exportType === 'mistakes'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-slate-200 hover:border-blue-300'
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <FileText className={`w-6 h-6 ${exportType === 'mistakes' ? 'text-blue-500' : 'text-slate-400'}`} />
                <span className="font-semibold text-slate-800">错题本</span>
              </div>
              <p className="text-sm text-slate-600">仅导出错题记录，方便复习薄弱知识点</p>
            </button>
          </div>

          {/* Date range (only for records) */}
          {exportType === 'records' && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                选择日期范围（可选）
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">开始日期</label>
                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={e => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                    className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">结束日期</label>
                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={e => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                    className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              {summary?.dateRange.start && (
                <p className="text-xs text-slate-400 mt-2">
                  数据范围：{summary.dateRange.start} 至 {summary.dateRange.end}
                </p>
              )}
            </div>
          )}

          {/* Export button */}
          <div className="flex items-center gap-4">
            <button
              onClick={handleExport}
              disabled={exporting}
              className="flex-1 md:flex-none px-8 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-semibold rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {exporting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  导出中...
                </>
              ) : (
                <>
                  <Download className="w-5 h-5" />
                  导出CSV文件
                </>
              )}
            </button>
            
            <AnimatePresence>
              {exportSuccess && (
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center gap-2 text-emerald-600"
                >
                  <CheckCircle className="w-5 h-5" />
                  <span>导出成功！</span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Tips */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-blue-50 rounded-2xl p-5"
        >
          <h3 className="font-semibold text-blue-800 mb-2">💡 导出说明</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• 导出文件为CSV格式，可用Excel或Numbers打开</li>
            <li>• 文件包含学习报告摘要，方便查看整体学习情况</li>
            <li>• 建议定期导出备份，记录学习进步</li>
          </ul>
        </motion.div>
      </div>
    </div>
  );
}
